/**
 * Drill Record Form - Clean version with proper styling
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertCircle, Calendar, Clock, Timer, MapPin } from 'lucide-react';
import { useCrew } from '../../../../hooks/useCrew';
import { getDrillDisplayName } from './utils/drillsHelpers';
import Dropdown from '../../../common/Dropdown';
import { DRILL_TYPES } from '../../../../config/vesselRequirements/domains/drills/requirements';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

const DrillRecordFormClean = ({ isOpen, onClose, onSave, vesselRequirements, preselectedDrillType, initialData = null, isSaving = false, canSignOff = false }) => {
  // FT-356c: only register the handler when the view is actually visible.
  useSetBackHandler(isOpen ? onClose : null);

  // Use the NEW unified crew system
  const { crew: allCrew, loading: crewLoading } = useCrew();
  const [crew, setCrew] = useState([]);
  const [activeCrew, setActiveCrew] = useState([]);
  const [formData, setFormData] = useState({
    type: preselectedDrillType || '',
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    duration: '30',
    location: '',
    participants: [],
    outcome: 'successful',
    notes: ''
  });

  const [signOffNow, setSignOffNow] = useState(false);

  // Validation errors
  const [errors, setErrors] = useState({});

  // FT-112: Create drill type options for Dropdown - include ALL drill types
  // Registered drills shown first with ★ prefix, ad-hoc drills available below
  const drillTypeOptions = useMemo(() => {
    const options = [];

    // First: Registered drills (from vessel requirements) with ★ prefix
    if (vesselRequirements && vesselRequirements.length > 0) {
      vesselRequirements.forEach(req => {
        options.push({
          value: req.drill_type,
          label: `★ ${getDrillDisplayName(req.drill_type)}${req.compliance_level === 'Required' ? ' *' : ''}`
        });
      });
    }

    // Second: All other drill types for ad-hoc recording (not in vessel requirements)
    const registeredTypes = new Set(vesselRequirements?.map(req => req.drill_type) || []);
    Object.values(DRILL_TYPES).forEach(drillType => {
      if (!registeredTypes.has(drillType)) {
        options.push({
          value: drillType,
          label: getDrillDisplayName(drillType)
        });
      }
    });

    return options;
  }, [vesselRequirements]);

  // Outcome options
  const outcomeOptions = [
    { value: 'successful', label: 'Successful' },
    { value: 'failed', label: 'Failed' }
  ];

  // Load crew when form opens or crew data changes
  // FIX: Use crew length as stable dependency instead of array reference
  useEffect(() => {
    if (isOpen && allCrew) {
      setCrew(allCrew);
      // All crew are considered active in new system
      // Show all non-archived crew (includes both On Duty and Off Duty)
      setActiveCrew(allCrew.filter(c => c.status !== 'archived'));
    }
  }, [isOpen, allCrew?.length]); // Use length instead of array to avoid reference changes

  // Update drill type if preselected
  useEffect(() => {
    if (preselectedDrillType) {
      setFormData(prev => ({ ...prev, type: preselectedDrillType }));
    }
  }, [preselectedDrillType]);

  // Initialize form with existing data for editing
  useEffect(() => {
    if (initialData) {
      // Extract participant IDs from initialData
      const participantIds = initialData.participants?.map(p =>
        typeof p === 'string' ? p : p.id
      ) || [];

      setFormData({
        type: initialData.type || '',
        date: initialData.date || new Date().toISOString().split('T')[0],
        time: initialData.time || new Date().toTimeString().slice(0, 5),
        duration: (initialData.duration || '30').toString(),
        location: initialData.location || '',
        participants: participantIds,
        outcome: initialData.outcome?.toLowerCase() || 'successful',
        notes: initialData.notes || ''
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.type) {
      newErrors.type = 'Drill type is required. Please select from the dropdown.';
    }

    if (formData.participants.length === 0) {
      newErrors.participants = 'At least one participant is required. Select crew members below.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const drillRecord = {
      ...formData,
      id: `drill-${Date.now()}`,
      timestamp: new Date().toISOString(),
      duration: parseInt(formData.duration) || 30,
      participants: formData.participants.map(crewId => {
        const member = crew.find(c => c.id === crewId);
        return {
          id: crewId,
          name: member?.name || member?.full_name || 'Unknown',
          position: member?.position || ''
        };
      }),
      // FT-06d P2b: Include sign-off flag (only for new drills, not edits)
      signOffNow: !initialData && signOffNow
    };

    onSave(drillRecord);
    onClose();
  };

  const toggleParticipant = (crewId) => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.includes(crewId)
        ? prev.participants.filter(id => id !== crewId)
        : [...prev.participants, crewId]
    }));
  };

  const selectAll = () => {
    setFormData(prev => ({
      ...prev,
      participants: prev.participants.length === activeCrew.length 
        ? [] 
        : activeCrew.map(c => c.id)
    }));
  };

  if (!isOpen) return null;

  return (
    // FT-363: pb-24 md:pb-0 prevents Save Drill Record button from being
    // hidden behind fixed MobileBottomBar (~64px + safe-area) on mobile.
    // Same FT-83 pattern applied to Crew / Defects / Maintenance forms.
    <div data-testid="drill-record-form-root" className="min-h-screen bg-gray-50 pb-24 md:pb-0">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* FT-356c: header raw ArrowLeft back button removed — bottom bar owns Back. */}
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">{initialData ? 'Edit Drill' : 'Record Drill'}</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content - Structured in sections */}
      <form onSubmit={handleSubmit} className="flex flex-col flex-1">
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-6 space-y-6">
            {/* Section 1: Drill Details */}
            <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
              <div className="bg-slate-700 px-5 py-3">
                <h2 className="text-base font-semibold text-white">Drill Details</h2>
                <p className="mt-1 text-sm text-slate-300">Basic information about the drill</p>
              </div>
              <div className="p-6 space-y-4">
                {/* Drill Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Drill Type <span className="text-red-500">*</span>
                  </label>
                  <Dropdown
                    value={formData.type}
                    onChange={(value) => {
                      setFormData({ ...formData, type: value });
                      if (errors.type) {
                        setErrors(prev => ({ ...prev, type: '' }));
                      }
                    }}
                    options={drillTypeOptions}
                    placeholder="Select drill type..."
                    width="w-full"
                    error={!!errors.type}
                  />
                  {errors.type ? (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.type}
                    </p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">★ Registered drills • * Required • Other drills can be recorded ad-hoc</p>
                  )}
                </div>

                {/* Date, Time, Duration */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Calendar className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      max={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Clock className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                      Time
                    </label>
                    <input
                      type="time"
                      value={formData.time}
                      onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Timer className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                      min="1"
                      max="480"
                    />
                  </div>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                    Location
                  </label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Bridge, Engine Room, Main Deck"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
              </div>
            </section>

            {/* Section 2: Participants */}
            <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
              <div className="bg-slate-700 px-5 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-white">Participants</h2>
                    <p className="mt-1 text-sm text-slate-300">Select crew members who participated</p>
                  </div>
                  {activeCrew.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        selectAll();
                        if (errors.participants) {
                          setErrors(prev => ({ ...prev, participants: '' }));
                        }
                      }}
                      className="text-sm text-white hover:text-slate-200 font-medium"
                    >
                      {formData.participants.length === activeCrew.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>
              </div>
              <div className="p-6">
                {activeCrew.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">No active crew members found</p>
                    <p className="text-xs text-gray-400 mt-1">Add crew members in the Crew section first</p>
                  </div>
                ) : (
                  <div>
                    <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 max-h-64 overflow-y-auto p-2 rounded-lg ${
                      errors.participants ? 'ring-2 ring-red-300' : ''
                    }`}>
                      {activeCrew.map(member => {
                        const isSelected = formData.participants.includes(member.id);
                        return (
                          <button
                            key={member.id}
                            type="button"
                            onClick={() => {
                              toggleParticipant(member.id);
                              if (errors.participants) {
                                setErrors(prev => ({ ...prev, participants: '' }));
                              }
                            }}
                            className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                              isSelected
                                ? 'border-cyan-500 bg-cyan-50 text-cyan-900'
                                : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'
                            }`}
                          >
                            <div className="font-medium text-sm">{member.name || member.full_name}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{member.position || 'Crew'}</div>
                          </button>
                        );
                      })}
                    </div>
                    {errors.participants && (
                      <p className="mt-3 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.participants}
                      </p>
                    )}
                    <p className="mt-3 text-xs text-gray-500">
                      {formData.participants.length} participant{formData.participants.length !== 1 ? 's' : ''} selected
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Section 3: Outcome & Notes */}
            <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
              <div className="bg-slate-700 px-5 py-3">
                <h2 className="text-base font-semibold text-white">Outcome & Notes</h2>
                <p className="mt-1 text-sm text-slate-300">Record the result and any observations</p>
              </div>
              <div className="p-6 space-y-4">
                {/* Outcome */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Outcome
                  </label>
                  <Dropdown
                    value={formData.outcome}
                    onChange={(value) => setFormData({ ...formData, outcome: value })}
                    options={outcomeOptions}
                    width="w-full md:w-64"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    placeholder="Record any observations, issues, or improvements needed..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 resize-none"
                  />
                  <p className="mt-1 text-xs text-gray-500">Optional: Add any relevant details about the drill</p>
                </div>

                {canSignOff && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={signOffNow}
                      onChange={e => setSignOffNow(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    Sign off now
                  </label>
                )}
              </div>
            </section>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center gap-2"
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSaving ? 'Saving...' : (initialData ? 'Update Drill Record' : 'Save Drill Record')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DrillRecordFormClean;